import Regimen, { AssignedExercise } from "../classes/regimen";

const GRID_SEQUENCE_REPORT_CONTEXT_FIELDS = [
  "grid_sequence_id",
  "grid_sequence_assignment_id",
  "grid_sequence_step_id",
  "grid_sequence_step_activation_id",
] as const;

export function replaceRegimen(
  regimens: Regimen[],
  updatedRegimen: Regimen,
): Regimen[] {
  const index = regimens.findIndex((regimen) => regimen.id === updatedRegimen.id);
  if (index === -1) return [...regimens, updatedRegimen];
  return regimens.map((regimen, candidateIndex) =>
    candidateIndex === index ? updatedRegimen : regimen);
}

/** Merges session reports into an existing progress list, keyed by report `id`. */
export function mergeProgressReports<T extends { id?: string | null }>(
  existing: readonly T[] | null | undefined,
  sessionReports: readonly (T | null | undefined)[],
): T[] {
  const merged = new Map<string, T>();
  for (const report of existing || []) {
    if (report && report.id) {
      merged.set(report.id, report);
    }
  }
  for (const report of sessionReports) {
    if (report && report.id) {
      merged.set(report.id, report);
    }
  }
  return Array.from(merged.values());
}

export function gridSequenceReportContext(
  exercise: AssignedExercise,
  transitionedActivationIds: readonly string[] = [],
): Record<string, string> {
  if (
    exercise.grid_sequence_step_activation_id
    && transitionedActivationIds.includes(exercise.grid_sequence_step_activation_id)
  ) {
    return {};
  }
  if (!GRID_SEQUENCE_REPORT_CONTEXT_FIELDS.every((field) => exercise[field])) return {};
  return GRID_SEQUENCE_REPORT_CONTEXT_FIELDS.reduce<Record<string, string>>((context, field) => {
    context[field] = exercise[field] as string;
    return context;
  }, {});
}

export function withoutGridSequenceReportContext<T extends Record<string, any>>(report: T): T {
  const result = { ...report };
  GRID_SEQUENCE_REPORT_CONTEXT_FIELDS.forEach((field) => delete result[field]);
  return result;
}

export function reportMatchesProjectedExercise(
  report: {
    regimen_instance_id?: string | null;
    grid_sequence_step_activation_id?: string | null;
  },
  exercise: AssignedExercise,
): boolean {
  if (report.regimen_instance_id !== exercise.uuid) return false;
  if (!exercise.grid_sequence_assignment_id) return true;
  return Boolean(
    exercise.grid_sequence_step_activation_id
    && report.grid_sequence_step_activation_id === exercise.grid_sequence_step_activation_id,
  );
}

function activeStepExercises(assignment: AssignedExercise): AssignedExercise[] {
  if (!assignment.grid_sequence || assignment.active_step === "complete") {
    return [];
  }

  const steps = assignment.grid_sequence.sequence || [];
  const activeStep =
    assignment.active_step && assignment.active_step !== "uninitialized"
      ? steps.find((step) => step.id === assignment.active_step)
      : steps[0];

  if (!activeStep) {
    return [];
  }

  return (activeStep.exercises || [])
    .filter((exercise) => exercise.exercise_type !== "email")
    .map((exercise) => ({
      ...exercise,
      grid_sequence_id: assignment.grid_sequence!.id,
      grid_sequence_assignment_id: assignment.uuid,
      grid_sequence_step_id: activeStep.id,
      grid_sequence_step_activation_id: assignment.active_step_activation_id || null,
    }));
}

function activeScheduleStageExercises(assignment: AssignedExercise): AssignedExercise[] {
  if (!assignment.grid_schedule || !assignment.grid_current_stage) return [];
  const stage = assignment.grid_schedule.grid.find(
    (candidate) => candidate.id === assignment.grid_current_stage,
  );
  return stage ? stage.exercises : [];
}

/** Visible child exercises for a grid container's current set / stage / step. */
export function currentContainerExercises(assignment: AssignedExercise): AssignedExercise[] {
  let children: AssignedExercise[] | null = null;
  if (assignment.exercise_type === "grid_set") {
    children = assignment.grid_set ? assignment.grid_set.exercises : [];
  } else if (assignment.exercise_type === "grid_schedule") {
    children = activeScheduleStageExercises(assignment);
  } else if (assignment.exercise_type === "grid_sequence") {
    children = activeStepExercises(assignment);
  } else {
    return [];
  }
  return children.filter((exercise) => exercise.exercise_type !== "email");
}

function projectAssignment(assignment: AssignedExercise): AssignedExercise[] {
  if (
    assignment.exercise_type === "grid_set"
    || assignment.exercise_type === "grid_schedule"
    || assignment.exercise_type === "grid_sequence"
  ) {
    return currentContainerExercises(assignment);
  }
  return assignment.exercise_type === "email" ? [] : [assignment];
}

export function projectRegimenExercises(
  assignments: AssignedExercise[],
): AssignedExercise[] {
  return assignments.flatMap(projectAssignment);
}

export function replaceProjectedExercise(
  assignments: AssignedExercise[],
  updatedExercise: AssignedExercise,
): boolean {
  for (let index = 0; index < assignments.length; index += 1) {
    const assignment = assignments[index];
    if (assignment.uuid === updatedExercise.uuid) {
      assignments[index] = updatedExercise;
      return true;
    }
    if (
      assignment.grid_set
      && assignment.grid_set.exercises.some((exercise, childIndex) => {
        if (exercise.uuid !== updatedExercise.uuid) return false;
        assignment.grid_set!.exercises[childIndex] = updatedExercise;
        return true;
      })
    ) {
      return true;
    }
    if (assignment.grid_schedule) {
      for (const stage of assignment.grid_schedule.grid) {
        const childIndex = stage.exercises.findIndex(
          (exercise) => exercise.uuid === updatedExercise.uuid,
        );
        if (childIndex !== -1) {
          stage.exercises[childIndex] = updatedExercise;
          return true;
        }
      }
    }
    if (assignment.grid_sequence) {
      for (const step of assignment.grid_sequence.sequence) {
        const childIndex = step.exercises.findIndex(
          (exercise) => exercise.uuid === updatedExercise.uuid,
        );
        if (childIndex !== -1) {
          step.exercises[childIndex] = updatedExercise;
          return true;
        }
      }
    }
  }
  return false;
}

export function assignedGridSchedules(
  regimen: { exercises?: AssignedExercise[] } | null | undefined,
): AssignedExercise[] {
  return (regimen?.exercises || []).filter((exercise) => exercise.exercise_type === "grid_schedule");
}

export function firstAssignedGridSchedule(
  regimen: { exercises?: AssignedExercise[] } | null | undefined,
): AssignedExercise | null {
  return assignedGridSchedules(regimen)[0] || null;
}
